import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search, X, Check } from 'lucide-react';
import Modal from '../Modal';
import { api } from '../../utils/upgradesApi';

const DEBOUNCE_MS = 250;

// "Nová úprava": pick the serial number(s) of one customer (search by SN, customer or city), optionally start from a template.
const NewUpgradeModal = ({ onClose, onCreated, templates = [], initialQuery = '' }) => {
  const [q, setQ] = useState(initialQuery);
  const [rows, setRows] = useState(null);
  const [selected, setSelected] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [title, setTitle] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const autoPicked = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(() => {
      api(`/machines?q=${encodeURIComponent(q)}`)
        .then((data) => {
          if (cancelled) return;
          setRows(data);
          // arriving from a machine detail: its serial number is preselected
          if (initialQuery && !autoPicked.current) {
            autoPicked.current = true;
            const exact = data.filter((m) => m.serial_number === initialQuery);
            if (exact.length === 1) setSelected(exact);
          }
        })
        .catch((e) => { if (!cancelled) setError(e.message); });
    }, DEBOUNCE_MS);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [q, initialQuery]);

  const customerId = selected[0]?.customer_id;
  const groups = useMemo(() => {
    const map = new Map();
    for (const m of rows || []) {
      if (!map.has(m.customer_id)) map.set(m.customer_id, { name: m.customer_name, city: m.city, machines: [] });
      map.get(m.customer_id).machines.push(m);
    }
    return [...map.entries()];
  }, [rows]);

  const isOn = (m) => selected.some((s) => s.id === m.id);
  const toggle = (m) => setSelected((cur) => (cur.some((s) => s.id === m.id) ? cur.filter((s) => s.id !== m.id) : [...cur, m]));

  const models = new Set(selected.map((m) => (/^TR4/i.test(m.model_type) ? 'TR4' : 'TR3')));
  const usableTemplates = templates.filter((t) => !t.model_type || models.size === 0 || (models.size === 1 && models.has(t.model_type)));

  const create = async () => {
    setBusy(true);
    setError('');
    try {
      const body = { machine_ids: selected.map((m) => m.id) };
      if (title.trim()) body.title = title.trim();
      if (templateId) body.template_id = Number(templateId);
      const data = await api('/', { method: 'POST', body });
      onCreated(data.id);
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  return (
    <Modal show onClose={onClose}>
      <div className="dlv dlv-card" role="dialog" aria-modal="true" aria-labelledby="upg-new-title" style={{ width: 'min(620px, calc(100vw - 32px))', maxHeight: '88vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
          <h2 id="upg-new-title" className="dlv-section-head__title" style={{ fontSize: 18 }}>Nová úprava – vyberte trenažér</h2>
          <button type="button" className="dlv-iconbtn" style={{ marginLeft: 'auto' }} onClick={onClose} aria-label="Zavřít"><X size={16} /></button>
        </div>

        <div style={{ position: 'relative', marginBottom: 8 }}>
          <Search size={16} style={{ position: 'absolute', left: 12, top: 14, color: '#999' }} aria-hidden="true" />
          <input className="dlv-input" style={{ paddingLeft: 36 }} autoFocus aria-label="Hledat trenažér" placeholder="Sériové číslo, zákazník nebo město…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        {error && <p role="alert" className="upg-error">{error}</p>}

        <div aria-live="polite">
          {!rows && <p className="dlv-hint">Načítám…</p>}
          {rows && rows.length === 0 && <p className="dlv-hint">Nic nenalezeno.</p>}
          {groups.map(([cid, g]) => (
            <div key={cid}>
              <div className="upg-group">{g.name}{g.city ? ` · ${g.city}` : ''}</div>
              {g.machines.map((m) => {
                const other = customerId && customerId !== m.customer_id;
                return (
                  <button key={m.id} type="button" className="upg-pick" aria-pressed={isOn(m)} disabled={Boolean(other)} onClick={() => toggle(m)}
                    title={other ? 'Jedna úprava může patřit jen jednomu zákazníkovi' : undefined}>
                    <span className="upg-pick__box" aria-hidden="true">{isOn(m) && <Check size={14} />}</span>
                    <span className="dlv-tag" style={{ background: '#111' }}>{m.model_type}</span>
                    <strong>SN {m.serial_number}</strong>
                    {m.site_name && <span className="upg-muted">{m.site_name}</span>}
                    {m.open_upgrades > 0 && <span className="upg-flag upg-flag--info" style={{ marginLeft: 'auto' }}>už má rozpracovanou úpravu</span>}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        {selected.length > 0 && (
          <div className="upg-fields" style={{ marginTop: 16 }}>
            <div className="full">
              <label className="dlv-label" htmlFor="upg-new-name">Název úpravy (nepovinné)</label>
              <input id="upg-new-name" className="dlv-input" value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} placeholder="Např. Výměna tabletu" />
            </div>
            {usableTemplates.length > 0 && (
              <div className="full">
                <label className="dlv-label" htmlFor="upg-new-tpl">Začít ze šablony změn</label>
                <select id="upg-new-tpl" className="dlv-input" value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
                  <option value="">Bez šablony (prázdný výpis změn)</option>
                  {usableTemplates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
          <button type="button" className="dlv-btn" onClick={onClose}>Zrušit</button>
          <button type="button" className="dlv-btn dlv-btn--primary" disabled={selected.length === 0 || busy} onClick={create}>
            {busy ? 'Zakládám…' : `Založit úpravu${selected.length > 1 ? ` (${selected.length} trenažéry)` : ''}`}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default NewUpgradeModal;
